/*
  Shopflo checkout — every route to checkout opens Shopflo instead of Shopify.

  Loaded from layout/theme.liquid when Theme settings > Shopflo checkout is on,
  right after Shopflo's own bridge script
  (https://bridge.shopflo.com/js/shopflo.bundle.js), which defines
  window.handleFloCheckoutBtn() and finds the store by its domain.

  This is the same hook the "Shopflo X Canyon" theme uses, done in one place
  instead of by rewriting each button:

    - Any Checkout button (name="checkout": cart drawer, cart page, the
      add-to-cart popup) or a link to /checkout is caught on click and opens
      Shopflo.
    - "Buy it now" (snippets/buy-buttons.liquid, [data-flo-buy-now]) adds the
      product with the page's choices — including "Gift: Yes" — then opens
      Shopflo.

  Our own cart drawer stays: gift wrap, rewards and add-ons all live in the
  Shopify cart, which is exactly what Shopflo reads. Before opening Shopflo the
  gift wrap lines are brought in line with the bag (assets/cart-giftwrap.js),
  so the wrap charge is never missing or stale.

  FALLBACK: if Shopflo's script has not loaded (blocked, offline, outage), the
  buttons do what they always did — normal Shopify checkout. A sale never
  stops because a third-party script failed.
*/
(() => {
  if (window.tyoFloCheckout) return;

  const floReady = () => typeof window.handleFloCheckoutBtn === 'function';

  const settleWraps = async () => {
    try {
      await window.tyoSyncWraps?.();
    } catch (error) {
      /* the bag is still valid without it */
    }
  };

  const openFlo = async () => {
    await settleWraps();
    window.handleFloCheckoutBtn();
  };

  window.tyoFloCheckout = openFlo;

  /* ----------------------------------------------- Cart icon / links

     Shopflo's bridge hooks clicks on cart links (/cart) and slides in ITS
     own cart over ours. We want our bag (gift wrap, rewards, add-ons) and
     Shopflo only at Checkout. So a cart-link click is handled here first —
     capture phase on window, which runs before anything on the page — and
     stopped before Shopflo's hook sees it; our drawer is then opened by hand
     (our own header listener is stopped too, so it has to be).

     No drawer on the page (cart type set to "page"): the link is left alone
     and goes to /cart as normal, where Shopflo is still only at Checkout. */
  window.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('#cart-icon-bubble, a[href="/cart"], a[href$="/cart"]');
      if (!link) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      drawer.open(link);
    },
    true
  );

  /* ------------------------------------------------- Checkout buttons */

  document.addEventListener(
    'click',
    async (event) => {
      const trigger = event.target.closest('button[name="checkout"], a[href$="/checkout"]');
      if (!trigger) return;
      // Shopflo not loaded: let the button go to Shopify checkout as before.
      if (!floReady()) return;

      event.preventDefault();
      event.stopPropagation();
      if (trigger.disabled || trigger.getAttribute('aria-busy') === 'true') return;

      trigger.setAttribute('aria-busy', 'true');
      try {
        await openFlo();
      } finally {
        trigger.removeAttribute('aria-busy');
      }
    },
    true
  );

  /* ------------------------------------------------------- Buy it now */

  const showError = (form, message) => {
    const box = form
      .closest('product-form')
      ?.querySelector('.product-form__error-message-wrapper');
    const text = box?.querySelector('.product-form__error-message');
    if (!box || !text) return;
    text.textContent = message;
    box.hidden = false;
  };

  /*
    BUY IT NOW CHECKS OUT ONLY THE CHOSEN PRODUCT — the cart is left alone.

    Shopflo can only check out "the cart" (its own buy-now even replaces the
    cart outright, losing what the shopper had). So:

      1. the shopper's cart is saved (sessionStorage, so it survives a reload
         or the thank-you page),
      2. the cart becomes just this product — plus its gift wrap when
         "Make it a gift" is ticked — and Shopflo opens on that,
      3. when Shopflo closes (paid, or backed out), the saved cart is put
         back exactly as it was, properties and all.

    The bag count and drawer are never re-rendered in between, so the shopper
    never sees their cart change.
  */
  const SAVED_KEY = 'tyoFloSavedCart';
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const getCart = async () => (await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' })).json();
  const clearCart = () => fetch(`${window.routes.cart_url}/clear.js`, { method: 'POST', headers: json });
  const addItems = (items) =>
    fetch(`${window.routes.cart_add_url}.js`, { method: 'POST', headers: json, body: JSON.stringify({ items }) });

  const restoreSavedCart = async () => {
    let saved;
    try {
      saved = JSON.parse(sessionStorage.getItem(SAVED_KEY) || 'null');
    } catch (error) {
      saved = null;
    }
    if (!saved) return;
    sessionStorage.removeItem(SAVED_KEY);

    await clearCart();
    if (saved.items.length) await addItems(saved.items);
    // Bring the page's bag in line, quietly (no slide-in).
    await window.tyoCartRefresh?.();
  };

  // A cart left saved — the page was reloaded or the shopper came back from
  // the thank-you page — is put back as soon as the store loads.
  if (sessionStorage.getItem(SAVED_KEY)) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreSavedCart);
    else restoreSavedCart();
  }

  // Shopflo's checkout is open while #flo-checkout-wrapper has a size (it is
  // 0 x 0 when closed). Wait for it to open, then for it to close.
  const floOpen = () => {
    const wrapper = document.getElementById('flo-checkout-wrapper');
    if (!wrapper) return false;
    const box = wrapper.getBoundingClientRect();
    return box.width > 0 && box.height > 0;
  };

  const whenFloCloses = () =>
    new Promise((resolve) => {
      let seenOpen = false;
      const started = Date.now();
      const timer = setInterval(() => {
        if (floOpen()) seenOpen = true;
        const gaveUp = !seenOpen && Date.now() - started > 15000;
        if ((seenOpen && !floOpen()) || gaveUp) {
          clearInterval(timer);
          resolve();
        }
      }, 400);
    });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-flo-buy-now]');
    if (!button) return;
    event.preventDefault();

    const form = document.getElementById(button.dataset.form);
    if (!form || button.disabled) return;

    // Sold out / unavailable: the Add to cart button says so; do nothing.
    const add = form.querySelector('[name="add"]');
    if (add?.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      // 1. Save the shopper's cart — unless one is already saved: then a
      //    Buy it now is already in progress and the cart now is only that
      //    product, so overwriting would lose the shopper's real cart.
      if (!sessionStorage.getItem(SAVED_KEY)) {
        const cart = await getCart();
        sessionStorage.setItem(
          SAVED_KEY,
          JSON.stringify({
            items: cart.items.map((line) => ({
              id: line.variant_id,
              quantity: line.quantity,
              properties: line.properties || {},
            })),
          })
        );
      }

      // 2. Just this product. FormData(form) also picks up inputs tied to the
      //    form by form="" — the quantity box and the "Make it a gift" tick.
      await clearCart();
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        await restoreSavedCart();
        showError(form, data.description || data.message || 'Could not add this item.');
        return;
      }

      // Its gift wrap, when ticked: one wrap per unit, linked to the variant
      // (same shape as assets/cart-giftwrap.js makes).
      // The id string from the button, NOT form.id: the product form has an
      // input named "id" (the variant), so form.id returns that input.
      const gift = document.querySelector(
        `input[form="${button.dataset.form}"][data-tyo-wrap-variant]:checked`
      );
      if (gift) {
        await addItems([
          {
            id: Number(gift.dataset.tyoWrapVariant),
            quantity: data.quantity,
            properties: { _tyo_wrap_for: String(data.variant_id) },
          },
        ]);
      }

      if (!floReady()) {
        // Shopflo missing: Shopify checkout with just this product; the cart
        // comes back when the shopper returns to the store.
        window.location.href = '/checkout';
        return;
      }

      // 3. Open Shopflo on this product only, then put the cart back.
      window.handleFloCheckoutBtn();
      await whenFloCloses();
      await restoreSavedCart();
    } catch (error) {
      console.error('[shopflo] buy it now failed', error);
      await restoreSavedCart();
      showError(form, 'Something went wrong. Please try again.');
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  });
})();
